import Delaunator from 'delaunator';
import { FACE_HEIGHT as H, FACE_WIDTH as W } from '@karozik/shared';

/**
 * Face morphing on a 2D canvas, driven by MediaPipe's face mesh (468 points
 * found on the guest's phone). Both faces are cut into the same triangles and
 * each triangle is moved onto a target shape with an affine transform, so eyes,
 * nose and mouth land exactly on top of each other before mixing.
 */

/** Flat x,y landmark array, in the 360×450 selfie frame. */
export type Landmarks = ArrayLike<number>;

// MediaPipe face mesh indices (the person's right eye is on the image's left).
const RIGHT_EYE = [33, 7, 163, 144, 145, 153, 154, 155, 133, 173, 157, 158, 159, 160, 161, 246, 46, 53, 52, 65, 55, 70, 63, 105, 66, 107];
const LEFT_EYE = [263, 249, 390, 373, 374, 380, 381, 382, 362, 398, 384, 385, 386, 387, 388, 466, 276, 283, 282, 295, 285, 300, 293, 334, 296, 336];
const NOSE = [1, 2, 4, 5, 19, 45, 48, 49, 64, 94, 97, 98, 115, 129, 195, 220, 275, 278, 279, 294, 326, 327, 344, 358, 440];
const MOUTH = [
  61, 146, 91, 181, 84, 17, 314, 405, 321, 375, 291, 409, 270, 269, 267, 0, 37, 39, 40, 185, 78, 95, 88, 178, 87, 14, 317, 402, 318, 324, 308, 415, 310,
  311, 312, 13, 82, 81, 80, 191,
];

/** Fixed points on the frame's edges, so the background and hair follow along. */
const BORDER = [0, 0, W / 2, 0, W, 0, W, H / 2, W, H, W / 2, H, 0, H, 0, H / 2];

function withBorder(lm: Landmarks): number[] {
  return [...Array.from(lm), ...BORDER];
}

function lerp(a: number[], b: number[], t: number): number[] {
  return a.map((v, i) => v + (b[i]! - v) * t);
}

/** Scales a group of points around its centre. */
function inflate(pts: number[], group: number[], k: number) {
  let cx = 0;
  let cy = 0;
  for (const i of group) {
    cx += pts[i * 2]!;
    cy += pts[i * 2 + 1]!;
  }
  cx /= group.length;
  cy /= group.length;
  for (const i of group) {
    pts[i * 2] = cx + (pts[i * 2]! - cx) * k;
    pts[i * 2 + 1] = cy + (pts[i * 2 + 1]! - cy) * k;
  }
}

const EYE_RING_RIGHT = [33, 7, 163, 144, 145, 153, 154, 155, 133, 173, 157, 158, 159, 160, 161, 246];
const EYE_RING_LEFT = [263, 249, 390, 373, 374, 380, 381, 382, 362, 398, 384, 385, 386, 387, 388, 466];
const MOUTH_OUTER = [61, 146, 91, 181, 84, 17, 314, 405, 321, 375, 291, 409, 270, 269, 267, 0, 37, 39, 40, 185];

/**
 * A magnifying glass centred on a feature: points near the centre move out by
 * up to `a`× their distance, fading to nothing at radius `r`. The falloff
 * (1 − d/r)² keeps the mapping fold-free for a ≤ 3, so the skin around the
 * feature stretches smoothly instead of tearing.
 */
function bulge(pts: number[], group: number[], radiusFactor: number, a: number) {
  let cx = 0;
  let cy = 0;
  for (const i of group) {
    cx += pts[i * 2]!;
    cy += pts[i * 2 + 1]!;
  }
  cx /= group.length;
  cy /= group.length;
  let spread = 0;
  for (const i of group) spread = Math.max(spread, Math.hypot(pts[i * 2]! - cx, pts[i * 2 + 1]! - cy));
  const r = spread * radiusFactor;
  for (let i = 0; i < pts.length / 2; i++) {
    const dx = pts[i * 2]! - cx;
    const dy = pts[i * 2 + 1]! - cy;
    const u = Math.hypot(dx, dy) / r;
    if (u >= 1) continue;
    const f = 1 + a * (1 - u) ** 2;
    pts[i * 2] = cx + dx * f;
    pts[i * 2 + 1] = cy + dy * f;
  }
}

/** Big eyes, bigger nose, big mouth: a fairground caricature. Only the face mesh moves, never the frame's border. */
function caricature(pts: number[], k: number): number[] {
  const out = [...pts];
  const a = Math.min(3, Math.max(0, k - 1));
  const mesh = out.slice(0, 468 * 2);
  bulge(mesh, EYE_RING_RIGHT, 2.2, a);
  bulge(mesh, EYE_RING_LEFT, 2.2, a);
  bulge(mesh, NOSE, 1.6, a * 1.2);
  bulge(mesh, MOUTH_OUTER, 1.7, a * 0.8);
  out.splice(0, mesh.length, ...mesh);
  return out;
}

/** The affine transform (canvas `setTransform` order) mapping triangle `s` onto triangle `d`. */
export function affine(s: number[], d: number[]): [number, number, number, number, number, number] | null {
  const [x0, y0, x1, y1, x2, y2] = s as [number, number, number, number, number, number];
  const [u0, v0, u1, v1, u2, v2] = d as [number, number, number, number, number, number];
  const den = x0 * (y1 - y2) + x1 * (y2 - y0) + x2 * (y0 - y1);
  if (Math.abs(den) < 1e-6) return null;
  const a = (u0 * (y1 - y2) + u1 * (y2 - y0) + u2 * (y0 - y1)) / den;
  const c = (u0 * (x2 - x1) + u1 * (x0 - x2) + u2 * (x1 - x0)) / den;
  const e = (u0 * (x1 * y2 - x2 * y1) + u1 * (x2 * y0 - x0 * y2) + u2 * (x0 * y1 - x1 * y0)) / den;
  const b = (v0 * (y1 - y2) + v1 * (y2 - y0) + v2 * (y0 - y1)) / den;
  const dd = (v0 * (x2 - x1) + v1 * (x0 - x2) + v2 * (x1 - x0)) / den;
  const f = (v0 * (x1 * y2 - x2 * y1) + v1 * (x2 * y0 - x0 * y2) + v2 * (x0 * y1 - x1 * y0)) / den;
  return [a, b, c, dd, e, f];
}

function canvas(): [HTMLCanvasElement, CanvasRenderingContext2D] {
  const c = document.createElement('canvas');
  c.width = W;
  c.height = H;
  return [c, c.getContext('2d')!];
}

/** Moves `img` from shape `src` to shape `dst`, triangle by triangle. */
function warp(img: CanvasImageSource, src: number[], dst: number[], tris: Uint32Array): HTMLCanvasElement {
  const [c, ctx] = canvas();
  ctx.drawImage(img, 0, 0, W, H);
  for (let t = 0; t < tris.length; t += 3) {
    const ids = [tris[t]!, tris[t + 1]!, tris[t + 2]!];
    const s = ids.flatMap((i) => [src[i * 2]!, src[i * 2 + 1]!]);
    const d = ids.flatMap((i) => [dst[i * 2]!, dst[i * 2 + 1]!]);
    const m = affine(s, d);
    if (!m) continue;
    // Grow the clip by a hair so neighbouring triangles overlap instead of leaving seams.
    const cx = (d[0]! + d[2]! + d[4]!) / 3;
    const cy = (d[1]! + d[3]! + d[5]!) / 3;
    ctx.save();
    ctx.beginPath();
    for (let p = 0; p < 3; p++) {
      const dx = d[p * 2]! - cx;
      const dy = d[p * 2 + 1]! - cy;
      const len = Math.hypot(dx, dy) || 1;
      const x = d[p * 2]! + (dx / len) * 0.8;
      const y = d[p * 2 + 1]! + (dy / len) * 0.8;
      if (p === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    }
    ctx.closePath();
    ctx.clip();
    ctx.setTransform(m[0], m[1], m[2], m[3], m[4], m[5]);
    // Only the source triangle's bounding box: much less to paint than the whole photo.
    const sx = Math.max(0, Math.floor(Math.min(s[0]!, s[2]!, s[4]!)) - 2);
    const sy = Math.max(0, Math.floor(Math.min(s[1]!, s[3]!, s[5]!)) - 2);
    const sw = Math.min(W, Math.ceil(Math.max(s[0]!, s[2]!, s[4]!)) + 2) - sx;
    const sh = Math.min(H, Math.ceil(Math.max(s[1]!, s[3]!, s[5]!)) + 2) - sy;
    if (sw > 0 && sh > 0) ctx.drawImage(img, sx, sy, sw, sh, sx, sy, sw, sh);
    ctx.restore();
  }
  return c;
}

function triangulate(pts: number[]): Uint32Array {
  return new Delaunator(Float64Array.from(pts)).triangles;
}

function hull(points: [number, number][]): [number, number][] {
  const p = [...points].sort((a, b) => a[0] - b[0] || a[1] - b[1]);
  const cross = (o: [number, number], a: [number, number], b: [number, number]) => (a[0] - o[0]) * (b[1] - o[1]) - (a[1] - o[1]) * (b[0] - o[0]);
  const lower: [number, number][] = [];
  for (const q of p) {
    while (lower.length >= 2 && cross(lower[lower.length - 2]!, lower[lower.length - 1]!, q) <= 0) lower.pop();
    lower.push(q);
  }
  const upper: [number, number][] = [];
  for (const q of p.reverse()) {
    while (upper.length >= 2 && cross(upper[upper.length - 2]!, upper[upper.length - 1]!, q) <= 0) upper.pop();
    upper.push(q);
  }
  return [...lower.slice(0, -1), ...upper.slice(0, -1)];
}

/** A soft mask over eyes and brows, nose and mouth of shape `pts`. */
function featureMask(pts: number[]): HTMLCanvasElement {
  const scale = 1 / 9;
  const small = document.createElement('canvas');
  small.width = Math.ceil(W * scale);
  small.height = Math.ceil(H * scale);
  const s = small.getContext('2d')!;
  s.fillStyle = '#000';
  for (const [group, grow] of [
    [RIGHT_EYE, 1.25],
    [LEFT_EYE, 1.25],
    [NOSE, 1.15],
    [MOUTH, 1.3],
  ] as const) {
    const g = [...pts];
    inflate(g, [...group], grow);
    const h = hull(group.map((i) => [g[i * 2]! * scale, g[i * 2 + 1]! * scale]));
    s.beginPath();
    h.forEach(([x, y], i) => (i ? s.lineTo(x, y) : s.moveTo(x, y)));
    s.closePath();
    s.fill();
  }
  // Upscaling the tiny mask with smoothing blurs its edges.
  const [c, ctx] = canvas();
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(small, 0, 0, W, H);
  return c;
}

/**
 * Gives `img` the colour statistics of `ref` under `mask` (per channel: same mean,
 * contrast scaled to match within bounds): pasted eyes and mouth then blend into
 * the host face instead of showing as darker or lighter patches.
 */
function matchTone(img: HTMLCanvasElement, ref: CanvasImageSource, mask: HTMLCanvasElement) {
  const [, rctx] = canvas();
  rctx.drawImage(ref, 0, 0, W, H);
  const ictx = img.getContext('2d')!;
  const px = ictx.getImageData(0, 0, W, H);
  const rp = rctx.getImageData(0, 0, W, H).data;
  const mp = mask.getContext('2d')!.getImageData(0, 0, W, H).data;
  const n = [0, 0];
  const mean = [0, 0, 0, 0, 0, 0];
  const sq = [0, 0, 0, 0, 0, 0];
  for (let i = 0; i < px.data.length; i += 4) {
    if (mp[i + 3]! < 128) continue;
    n[0]!++;
    for (let c = 0; c < 3; c++) {
      const v = px.data[i + c]!;
      const r = rp[i + c]!;
      mean[c]! += v;
      sq[c]! += v * v;
      mean[c + 3]! += r;
      sq[c + 3]! += r * r;
    }
  }
  if (n[0]! < 200) return;
  const stats = [0, 1, 2, 3, 4, 5].map((k) => {
    const m = mean[k]! / n[0]!;
    return { m, sd: Math.sqrt(Math.max(1, sq[k]! / n[0]! - m * m)) };
  });
  const map = [0, 1, 2].map((c) => {
    const src = stats[c]!;
    const dst = stats[c + 3]!;
    const k = Math.min(1.3, Math.max(0.7, dst.sd / src.sd));
    return (v: number) => Math.max(0, Math.min(255, (v - src.m) * k + dst.m));
  });
  for (let i = 0; i < px.data.length; i += 4) {
    for (let c = 0; c < 3; c++) px.data[i + c] = map[c]!(px.data[i + c]!);
  }
  ictx.putImageData(px, 0, 0);
}

export type WarpMode = 'morph' | 'features' | 'caricature';
export const WARP_MODES: readonly string[] = ['morph', 'features', 'caricature'];

/**
 * - morph: both faces moved to their average shape, then cross-faded (`ratio` of `b`).
 * - caricature: the same, with eyes, nose and mouth enlarged by `caricature`.
 * - features: `a`'s face with `b`'s eyes, nose and mouth fitted in place.
 */
export function renderWarped(
  out: HTMLCanvasElement,
  a: CanvasImageSource,
  b: CanvasImageSource,
  la: Landmarks,
  lb: Landmarks,
  mode: WarpMode,
  opts: { ratio: number; caricature: number },
) {
  const A = withBorder(la);
  const B = withBorder(lb);
  const x = out.getContext('2d')!;
  x.save();
  x.clearRect(0, 0, W, H);
  if (mode === 'features') {
    const tris = triangulate(A);
    const bOnA = warp(b, B, A, tris);
    const mask = featureMask(A);
    matchTone(bOnA, a, mask);
    const bctx = bOnA.getContext('2d')!;
    bctx.globalCompositeOperation = 'destination-in';
    bctx.drawImage(mask, 0, 0);
    x.drawImage(a, 0, 0, W, H);
    x.drawImage(bOnA, 0, 0);
  } else {
    let target = lerp(A, B, opts.ratio);
    if (mode === 'caricature') target = caricature(target, opts.caricature);
    const tris = triangulate(target);
    x.drawImage(warp(a, A, target, tris), 0, 0);
    x.globalAlpha = opts.ratio;
    x.drawImage(warp(b, B, target, tris), 0, 0);
  }
  x.restore();
}
